import * as React from 'react'
import { createPortal } from 'react-dom'
import { LOCALE } from '@foxrenderfarm/internals'
import {
  CurrentLocaleItem,
  LocaleItem,
  LocaleItemsWrapper,
  LocaleMenuBackdrop,
  LocaleMenuTriggerArrow,
  LocaleMenuWrapper,
  LocalePanelTitle,
  LocaleTriggerIconWrap,
} from './atoms'
import { useRef, useState, useEffect } from 'react'
import { LocaleClass } from '../../utils'
import { useClickAway } from '../../hooks'
import { DownArrowIcon, LocaleIconPlaceholder } from './svg'

const LOCALES_MAP = new Map([
  [LOCALE.EN, 'English'],
  [LOCALE.ES, 'España'],
  [LOCALE.PT, 'Português'],
  [LOCALE.DE, 'Deutsch'],
  [LOCALE.TR, 'Türkiye'],
  [LOCALE.JP, '日本語'],
  [LOCALE.IT, 'Italiano'],
  [LOCALE.FR, 'Français'],
  [LOCALE.RU, 'Россия'],
  [LOCALE.KR, '한국어'],
])

interface Props {
  /** 应用前缀，作为切换语言的根路径，如 share */
  prefix?: string
  currentLocale: LOCALE
  displayLanguages?: LOCALE[]
}
interface TLocaleList {
  value: string
  key: string
  link: string
}

export const LocaleMenu = ({ prefix, currentLocale, displayLanguages }: Props) => {
  const localeMenuWrapRef = useRef<HTMLDivElement | null>(null)
  const [showLocaleMenu, setShowLocaleMenu] = useState(false)
  const [localeList, setLocaleList] = useState<TLocaleList[]>([])

  useClickAway(localeMenuWrapRef, () => {
    showLocaleMenu && setShowLocaleMenu(false)
  })

  useEffect(() => {
    const localeKeys = displayLanguages ? displayLanguages : [...LOCALES_MAP.keys()]
    const pathname =
      typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/'
    const localeValues = Object.values(LOCALE)

    if (prefix && pathname.startsWith(`/${prefix}/`)) {
      const pathAfterPrefix = pathname.slice(`/${prefix}/`.length)
      const segments = pathAfterPrefix.split('/').filter(Boolean)
      const firstSegment = segments[0]
      const rest =
        firstSegment && localeValues.includes(firstSegment as LOCALE)
          ? pathAfterPrefix.slice(firstSegment.length + 1) || ''
          : pathAfterPrefix
      const restWithLeading = rest ? (rest.startsWith('/') ? rest : `/${rest}`) : '/'
      const localeList: TLocaleList[] = localeKeys.map((item) => ({
        value: LOCALES_MAP.get(item) as string,
        key: item,
        link:
          item === LOCALE.EN
            ? `/${prefix}${restWithLeading}`
            : `/${prefix}/${item}${restWithLeading}`,
      }))
      setLocaleList(localeList)
      return
    }

    const pathWithoutLocale = LocaleClass.getPathWithoutLocale() || '/'
    const localeList: TLocaleList[] = localeKeys.map((item) => ({
      value: LOCALES_MAP.get(item) as string,
      key: item,
      link: item === LOCALE.EN ? pathWithoutLocale : `/${item}${pathWithoutLocale}`,
    }))
    setLocaleList(localeList)
  }, [prefix, displayLanguages])

  return (
    <LocaleMenuWrapper ref={localeMenuWrapRef} $show={showLocaleMenu}>
      <CurrentLocaleItem $show={showLocaleMenu} onClick={() => setShowLocaleMenu(!showLocaleMenu)}>
        <LocaleTriggerIconWrap aria-hidden>
          <LocaleIconPlaceholder $show={showLocaleMenu} />
        </LocaleTriggerIconWrap>
        <span className='header-locale-text'>{LOCALES_MAP.get(currentLocale)}</span>
        <LocaleMenuTriggerArrow $show={showLocaleMenu}>
          <DownArrowIcon />
        </LocaleMenuTriggerArrow>
      </CurrentLocaleItem>
      {typeof document !== 'undefined' &&
        showLocaleMenu &&
        createPortal(
          <LocaleMenuBackdrop aria-hidden='true' onClick={() => setShowLocaleMenu(false)} />,
          document.body,
        )}
      <LocaleItemsWrapper $show={showLocaleMenu}>
        <LocalePanelTitle>Language</LocalePanelTitle>
        {localeList.map((item) => (
          <LocaleItem key={item.key} href={item.link} $isActive={currentLocale === item.key}>
            {item.value}
          </LocaleItem>
        ))}
      </LocaleItemsWrapper>
    </LocaleMenuWrapper>
  )
}
